import { supabase } from "@/integrations/supabase/client";
import { extractGraphErrorMessage, getInvokeErrorText } from "@/lib/invokeError";

/**
 * Helper BMAPI write — criar/editar templates WhatsApp Cloud.
 * UI completa (WhatsAppTemplatesPage) é trabalho futuro; use este helper em admin/scripts.
 *
 * Requer secret `WHATSAPP_CLOUD_TEMPLATES_WRITE_ENABLED=true` no Supabase.
 */

export type WhatsAppTemplateCategory = "MARKETING" | "UTILITY" | "AUTHENTICATION";

export type WhatsAppTemplateWriteAction = "create" | "edit" | "submit_for_review";

export interface WhatsAppTemplateWriteRequest {
  integration_id: string;
  action: WhatsAppTemplateWriteAction;
  name: string;
  language: string;
  category: WhatsAppTemplateCategory;
  components: Record<string, unknown>[];
  /** HSM id Meta — obrigatório para `edit`; opcional em `submit_for_review` (re-submit). */
  template_id?: string;
  /** Upsert em `whatsapp_cloud_templates` após sucesso Graph. */
  sync_after?: boolean;
}

export interface WhatsAppTemplateWriteResult {
  ok: boolean;
  action?: WhatsAppTemplateWriteAction;
  template_id?: string;
  graph?: Record<string, unknown>;
  synced?: boolean;
  error?: string;
  raw?: unknown;
}

function resolveTemplateWriteFailure(data: unknown, error: unknown): WhatsAppTemplateWriteResult {
  const payload = (data && typeof data === "object" ? data : null) as
    | (Record<string, unknown> & {
        error?: string;
        graph_error?: unknown;
        graph_status?: number;
      })
    | null;

  const graphMsg = extractGraphErrorMessage(payload?.graph_error);
  const graphStatus =
    typeof payload?.graph_status === "number" ? ` (Meta HTTP ${payload.graph_status})` : "";

  if (graphMsg) {
    return { ok: false, error: `${graphMsg}${graphStatus}`, raw: payload ?? data };
  }

  if (payload?.error) {
    return { ok: false, error: String(payload.error), raw: payload };
  }

  return { ok: false, error: getInvokeErrorText(error, data), raw: payload ?? data };
}

export async function invokeWhatsAppCloudTemplateWrite(
  request: WhatsAppTemplateWriteRequest,
): Promise<WhatsAppTemplateWriteResult> {
  const { data, error } = await supabase.functions.invoke("whatsapp-cloud-templates-write", {
    body: request,
  });

  if (data?.ok === false || data?.error) {
    return resolveTemplateWriteFailure(data, error);
  }

  if (error) {
    return resolveTemplateWriteFailure(data, error);
  }

  return {
    ok: true,
    action: data.action,
    template_id: data.template_id,
    graph: data.graph ?? {},
    synced: Boolean(data.synced),
  };
}
